/**
 * A trilha de auditoria, na forma que api e web combinam.
 *
 * Mora aqui, e não em cada ponta, porque a resposta desta rota deixou de ser
 * um array de registros e passou a ser um envelope com página e total: forma
 * combinada entre dois workspaces é o que o `packages/contracts` existe para
 * guardar. Antes desta mudança cada lado declarava o seu tipo à mão, e os dois
 * já teriam divergido em silêncio no primeiro campo novo.
 */
/** Quantos registros uma página traz, quando ninguém pede outro tamanho. */
export const REGISTROS_DE_AUDITORIA_POR_PAGINA = 50;
/**
 * Teto do tamanho de página que a rota aceita.
 *
 * Duzentos, e não ilimitado: quem chama escolhe quanto quer por página, mas
 * não pode pedir a tabela inteira numa requisição — a trilha só cresce, e um
 * `tamanho` sem teto devolveria o poder de varrer tudo que a paginação veio
 * tirar.
 */
export const MAXIMO_DE_REGISTROS_DE_AUDITORIA_POR_PAGINA = 200;
/** Teto do número de página, para `skip` absurdo não varrer a tabela à toa. */
export const MAXIMA_PAGINA_DE_AUDITORIA = 1000;
/**
 * Como cada entidade auditada se chama na tela.
 *
 * O banco guarda o nome do modelo — `RegraComissao`, `Implantacao` — porque é
 * o que o código registra ao auditar. Mas quem lê a trilha é a Administradora,
 * e o `GLOSSARY.md` é normativo quanto a isso: o termo do domínio é o nome que
 * aparece na tela. O valor técnico continua sendo o que vai para a rota; só o
 * rótulo muda.
 *
 * Entidade que não estiver aqui aparece com o nome técnico mesmo, que é feio
 * mas verdadeiro — melhor do que sumir do filtro por falta de tradução.
 */
export const ROTULO_ENTIDADE_AUDITADA = {
    Beneficiario: 'Beneficiário',
    Cadencia: 'Cadência',
    CadenciaAcao: 'Tentativa de cadência',
    CadenciaModelo: 'Roteiro de cadência',
    ChecklistItem: 'Item de conferência',
    ChecklistModelo: 'Roteiro de conferência',
    Comissao: 'Comissão',
    Contato: 'Contato',
    Documento: 'Documento',
    Implantacao: 'Implantação',
    Importacao: 'Importação',
    Integracao: 'Integração',
    Interacao: 'Interação',
    Lead: 'Lead',
    Movimentacao: 'Movimentação',
    Oportunidade: 'Oportunidade',
    Parceiro: 'Parceiro',
    Parcela: 'Parcela',
    RegraComissao: 'Regra de comissão',
    Renovacao: 'Renovação',
    Tarefa: 'Tarefa',
    Usuaria: 'Usuária',
};
/** O rótulo da entidade, ou o próprio nome técnico quando não há tradução. */
export function rotuloDaEntidadeAuditada(entidade) {
    return ROTULO_ENTIDADE_AUDITADA[entidade] ?? entidade;
}
/**
 * As ações que a trilha registra, espelhando o enum `AcaoAuditoria` do Prisma.
 *
 * Mora aqui pelo mesmo motivo dos demais enums: o frontend precisa delas e não
 * pode depender do pacote de banco. Ação nova entra nos dois lugares, junto da
 * migração — e o teste ao lado recusa a lista que ficar para trás.
 */
export const ACOES_AUDITADAS = [
    'CONTATO_CRIADO',
    'CONTATO_ATUALIZADO',
    'LEAD_CRIADO',
    'LEAD_IMPORTADO',
    'OPORTUNIDADE_CRIADA',
    'OPORTUNIDADE_EDITADA',
    'OPORTUNIDADE_ETAPA_ALTERADA',
    'OPORTUNIDADE_TRANSFERIDA',
    'INTERACAO_REGISTRADA',
    'CADENCIA_INICIADA',
    'CADENCIA_ACAO_EXECUTADA',
    'CADENCIA_PAUSADA',
    'CADENCIA_RETOMADA',
    'CADENCIA_CONCLUIDA',
    'CADENCIA_MODELO_ALTERADO',
    'CHECKLIST_MODELO_ALTERADO',
    'TAREFA_CRIADA',
    'TAREFA_CONCLUIDA',
    'NAO_CONTATAR_ATIVADO',
    'NAO_CONTATAR_REMOVIDO',
    'IMPORTACAO_CONFIRMADA',
    'CARTEIRA_IMPORTADA',
    'CONTRATO_ATUALIZADO',
    'PIPELINE_EXPORTADO',
    'USUARIA_CRIADA',
    'USUARIA_ATUALIZADA',
    'LOGIN_REALIZADO',
    'IMPLANTACAO_ATUALIZADA',
    'IMPLANTACAO_PRIMEIRO_BOLETO_PAGO',
    'IMPLANTACAO_PRIMEIRO_BOLETO_CORRIGIDO',
    'CHECKLIST_ITEM_ALTERADO',
    'PARCELA_REGISTRADA',
    'BENEFICIARIO_CRIADO',
    'BENEFICIARIO_ATUALIZADO',
    'MOVIMENTACAO_CRIADA',
    'MOVIMENTACAO_ATUALIZADA',
    'DOCUMENTO_ENVIADO',
    'DOCUMENTO_EXCLUIDO',
    'RENOVACAO_CRIADA',
    'RENOVACAO_ATUALIZADA',
    'LEMBRETE_REVISAO_REGISTRADO',
    'COMISSAO_RECEBIDA',
    'COMISSAO_ESTORNADA',
    'REGRA_COMISSAO_ALTERADA',
    'PARCEIRO_ALTERADO',
    'OPERADORA_ALTERADA',
    'ORIGEM_ALTERADA',
    'OPORTUNIDADE_EXCLUIDA',
    'LEAD_EXCLUIDO',
    'LEAD_RECEBIDO_DA_META',
    'LEADS_RESGATADOS_DA_META',
    'META_ALTERADA',
    'INTEGRACAO_ALTERADA',
    'PERFIL_ATUALIZADO',
    'SENHA_ALTERADA',
];
/**
 * Como cada ação auditada se lê na tela.
 *
 * O `Record` é fechado de propósito: ação nova no enum sem rótulo aqui não
 * compila. A trilha chegou a mostrar metade das ações em caixa-alta com
 * sublinhado — `IMPLANTACAO_PRIMEIRO_BOLETO_PAGO` era o marco que gera parcela
 * e comissão, e era assim que aparecia para quem foi entender de onde veio uma
 * comissão. O mapa ficava na tela, solto, e envelheceu calado.
 */
export const ROTULO_ACAO_AUDITADA = {
    CONTATO_CRIADO: 'Contato criado',
    CONTATO_ATUALIZADO: 'Contato atualizado',
    LEAD_CRIADO: 'Lead criado',
    LEAD_IMPORTADO: 'Lead importado',
    OPORTUNIDADE_CRIADA: 'Oportunidade criada',
    OPORTUNIDADE_EDITADA: 'Oportunidade editada',
    OPORTUNIDADE_ETAPA_ALTERADA: 'Etapa alterada',
    OPORTUNIDADE_TRANSFERIDA: 'Oportunidade transferida',
    INTERACAO_REGISTRADA: 'Interação registrada',
    CADENCIA_INICIADA: 'Cadência iniciada',
    CADENCIA_ACAO_EXECUTADA: 'Tentativa executada',
    CADENCIA_PAUSADA: 'Cadência pausada',
    CADENCIA_RETOMADA: 'Cadência retomada',
    CADENCIA_CONCLUIDA: 'Cadência concluída',
    CADENCIA_MODELO_ALTERADO: 'Roteiro de cadência alterado',
    CHECKLIST_MODELO_ALTERADO: 'Roteiro de conferência alterado',
    TAREFA_CRIADA: 'Tarefa criada',
    TAREFA_CONCLUIDA: 'Tarefa concluída',
    NAO_CONTATAR_ATIVADO: 'Bloqueio de contato ativado',
    NAO_CONTATAR_REMOVIDO: 'Bloqueio de contato removido',
    IMPORTACAO_CONFIRMADA: 'Importação confirmada',
    CARTEIRA_IMPORTADA: 'Carteira de clientes importada',
    CONTRATO_ATUALIZADO: 'Contrato atualizado',
    PIPELINE_EXPORTADO: 'Pipeline baixado em planilha',
    USUARIA_CRIADA: 'Usuária criada',
    USUARIA_ATUALIZADA: 'Usuária atualizada',
    LOGIN_REALIZADO: 'Entrada no sistema',
    IMPLANTACAO_ATUALIZADA: 'Implantação atualizada',
    IMPLANTACAO_PRIMEIRO_BOLETO_PAGO: 'Primeiro boleto pago',
    IMPLANTACAO_PRIMEIRO_BOLETO_CORRIGIDO: 'Primeiro boleto corrigido',
    CHECKLIST_ITEM_ALTERADO: 'Item de conferência alterado',
    PARCELA_REGISTRADA: 'Parcela registrada',
    BENEFICIARIO_CRIADO: 'Beneficiário criado',
    BENEFICIARIO_ATUALIZADO: 'Beneficiário atualizado',
    MOVIMENTACAO_CRIADA: 'Movimentação criada',
    MOVIMENTACAO_ATUALIZADA: 'Movimentação atualizada',
    DOCUMENTO_ENVIADO: 'Documento enviado',
    DOCUMENTO_EXCLUIDO: 'Documento excluído',
    RENOVACAO_CRIADA: 'Renovação criada',
    RENOVACAO_ATUALIZADA: 'Renovação atualizada',
    LEMBRETE_REVISAO_REGISTRADO: 'Lembrete de revisão registrado',
    COMISSAO_RECEBIDA: 'Comissão recebida',
    COMISSAO_ESTORNADA: 'Comissão estornada',
    REGRA_COMISSAO_ALTERADA: 'Regra de comissão alterada',
    PARCEIRO_ALTERADO: 'Parceiro alterado',
    OPERADORA_ALTERADA: 'Operadora alterada',
    ORIGEM_ALTERADA: 'Origem alterada',
    OPORTUNIDADE_EXCLUIDA: 'Oportunidade excluída',
    LEAD_EXCLUIDO: 'Lead excluído',
    LEAD_RECEBIDO_DA_META: 'Lead recebido da Meta',
    LEADS_RESGATADOS_DA_META: 'Leads resgatados do Lead Center',
    META_ALTERADA: 'Meta alterada',
    INTEGRACAO_ALTERADA: 'Integração alterada',
    PERFIL_ATUALIZADO: 'Perfil atualizado pela própria usuária',
    SENHA_ALTERADA: 'Senha trocada pela própria usuária',
};
/** O rótulo da ação, ou o próprio nome técnico quando não há tradução. */
export function rotuloDaAcaoAuditada(acao) {
    return ROTULO_ACAO_AUDITADA[acao] ?? acao;
}
//# sourceMappingURL=auditoria.js.map